import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ScenarioId, ScenarioPhase, SessionState, WhatIfInputs } from '@/versions/v1/schema/scenario';
import type { PlantCadence } from '@/versions/v1/schema/unit-operation';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import {
  advance as advanceState,
  INITIAL_STATE,
  setCursor as setCursorState,
  setPhase as setPhaseState,
  setCadence as setCadenceState,
  setWhatIf as setWhatIfState,
  stepCursor as stepCursorState,
  toggleScenario as toggleScenarioState,
} from './ScenarioEngine';

type ScenarioContextValue = {
  phase: ScenarioPhase;
  state: SessionState;
  diagnosis: Diagnosis;
  advance: () => void;
  reset: () => void;
  set: (phase: ScenarioPhase) => void;
  stepCursor: (minutes: number) => void;
  setCursor: (at: SessionState['cursor']) => void;
  toggleScenario: (id: ScenarioId) => void;
  setWhatIf: (inputs: WhatIfInputs | null) => void;
  setCadence: (cadence: PlantCadence) => void;
};

const ScenarioContext = createContext<ScenarioContextValue | null>(null);

export function ScenarioProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(INITIAL_STATE);
  const diagnosis = useMemo(() => diagnose(state), [state]);
  const phase = diagnosis.phase;

  const advance = useCallback(() => setState((s) => advanceState(s)), []);
  const reset = useCallback(() => setState(INITIAL_STATE), []);
  const set = useCallback((next: ScenarioPhase) => setState((s) => setPhaseState(s, next)), []);
  const stepCursor = useCallback(
    (minutes: number) => setState((s) => stepCursorState(s, minutes)),
    [],
  );
  const setCursor = useCallback(
    (at: SessionState['cursor']) => setState((s) => setCursorState(s, at)),
    [],
  );
  const toggleScenario = useCallback(
    (id: ScenarioId) => setState((s) => toggleScenarioState(s, id)),
    [],
  );
  const setWhatIf = useCallback(
    (inputs: WhatIfInputs | null) => setState((s) => setWhatIfState(s, inputs)),
    [],
  );
  const setCadence = useCallback(
    (cadence: PlantCadence) => setState((s) => setCadenceState(s, cadence)),
    [],
  );

  const value = useMemo(
    () => ({
      phase,
      state,
      diagnosis,
      advance,
      reset,
      set,
      stepCursor,
      setCursor,
      toggleScenario,
      setWhatIf,
      setCadence,
    }),
    [phase, state, diagnosis, advance, reset, set, stepCursor, setCursor, toggleScenario, setWhatIf, setCadence],
  );

  return <ScenarioContext.Provider value={value}>{children}</ScenarioContext.Provider>;
}

export function useScenario() {
  const ctx = useContext(ScenarioContext);
  if (!ctx) throw new Error('useScenario must be used inside <ScenarioProvider>');
  return ctx;
}
